import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtomValue, useSetAtom, getDefaultStore } from "jotai";
import { Header } from "components/common/header/Header";
import Svgs from "assets/svgs";
import { useQuery } from "hooks/useQuery";
import { Device } from "common/device";
import {
    recoverStepAtom,
    recoverStepStackAtom,
    popRecoverStepAtom,
    resetRecoverData,
    recoverHistoryDepthAtom,
    decrementRecoverHistoryDepthAtom,
    isExitingRecovery,
    setRecoveryExiting,
} from "store/recoverStore";
// recoverStepStackAtom 은 lazy init 에서 getDefaultStore().set 으로 사용
import { RecoverStep1 } from "components/recover/RecoverStep1";
import { RecoverStep2 } from "components/recover/RecoverStep2";
import { RecoverStep3 } from "components/recover/RecoverStep3";
import { RecoverStep4 } from "components/recover/RecoverStep4";
import { RecoverStep5 } from "components/recover/RecoverStep5";
import { RecoverStep6 } from "components/recover/RecoverStep6";
import { ConfirmPopup } from "wiz-front-shared/components/confirmPopup/ConfirmPopup";
import { useBackInterceptor } from "hooks/useBackInterceptor";
import { requestApp, WEB_TO_APP } from "common/webview";
import './AccountRecovery.css';

export const AccountRecovery = () => {
    const { t } = useTranslation();
    const { navigate } = useQuery();

    // Recovery 흐름에서 /sign-up 또는 /signin 으로 빠져나간 뒤 device back 으로 돌아온 경우 /signin 으로 redirect.
    //   - __recoveryToSignup: Step4(미가입) → /sign-up 으로 진입할 때 set
    //   - __recoveryToSignIn: 결과/완료 page 에서 /signin 으로 진입할 때 set
    //   두 케이스 모두 /account-recovery 재마운트 시 stale 화면 노출 방지 위해 동일하게 redirect.
    const [redirectToSignIn] = useState(() => {
        if (typeof window === "undefined") return false;
        const exited = sessionStorage.getItem("__recoveryToSignup") === "1"
                    || sessionStorage.getItem("__recoveryToSignIn") === "1";
        if (exited) {
            sessionStorage.removeItem("__recoveryToSignup");
            sessionStorage.removeItem("__recoveryToSignIn");
            return true;
        }
        return false;
    });

    // 진입 시 atom 동기 reset — 자식 컴포넌트 첫 render 전에 실행.
    //   useEffect 는 첫 render 이후라 race: stale 값으로 자식의 useState 가 초기화되어 입력값이 남아 보임.
    //   lazy useState init 으로 children render 전에 atom 을 비워 fresh 상태 보장.
    useState(() => {
        if (redirectToSignIn) return null;
        const store = getDefaultStore();
        store.set(resetRecoverData);
        if (Device.countryCd !== "KR") {
            store.set(recoverStepStackAtom, [2]);
        }
        setRecoveryExiting(false);
        return null;
    });

    const step = useAtomValue(recoverStepAtom);
    const popStep = useSetAtom(popRecoverStepAtom);
    const historyDepth = useAtomValue(recoverHistoryDepthAtom);
    const decrementDepth = useSetAtom(decrementRecoverHistoryDepthAtom);

    // 결과/완료 step 에선 뒤로가기 숨김 (Step4 = 결과, Step6 = 완료)
    const hideBackBtnSteps = [4, 6];

    // Step5(본인인증 통과 후 비밀번호 입력) 이탈 방지 팝업.
    //   device back 은 useBackInterceptor 가 메시지 기반으로 가로채므로 webview history 의존성 없음.
    const [abortOpen, setAbortOpen] = useState(false);

    const handleBack = () => {
        if (step === 5) {
            // 본인인증 통과 + 비밀번호 입력 중 → 팝업으로 확인
            setAbortOpen(true);
            return;
        }
        if (step === 4 || step === 6) {
            // dead-end (가입이력없음/SNS 안내/재설정 완료) — 명시적 CTA 버튼만 허용.
            //   device back 으로 의도치 않게 빠져나가는 것 방지 + 사용자에게 next action 안내.
            requestApp(WEB_TO_APP.SHOW_TOAST, { title: t("common.deadEndToast") });
            return;
        }
        const prev = popStep();
        if (prev === null) {
            navigate(-1);
        }
    };

    // device 뒤로 가로채기 — 네이티브가 PRESS_BACK 메시지로 위임.
    useBackInterceptor(handleBack);

    // 이탈 확정 → /signin 으로. Step4/6 dead-end 와 동일 패턴.
    const exitRecovery = () => {
        setRecoveryExiting(true);
        window.history.go(-(historyDepth + 1));
    };

    useEffect(() => {
        if (redirectToSignIn) {
            // /ar 의 모든 history entry (initial nav + pushState 들) 를 한 번에 pop 해서
            // /signin 위로 stale entry 가 남지 않도록 한다.
            navigate(-(historyDepth + 1));
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirectToSignIn, navigate]);

    useEffect(() => {
        if (redirectToSignIn) return;
        const onPopState = () => {
            // exit cascade 중에는 popstate 가 N 회 발생 — handler 가 popStep/depth/navigate 등 건드리지 않게 skip
            if (isExitingRecovery()) return;

            // closure 내 stale 값 대신 jotai store 에서 최신 step/depth 읽기
            const store = getDefaultStore();
            const currentStack = store.get(recoverStepStackAtom);
            const currentStep = currentStack[currentStack.length - 1];

            // Step4(결과)/Step6(완료) — dead-end 안내 화면. 직전 입력 화면 재진입 부자연스러움 → 메인(/signin) 직행
            if (currentStep === 4 || currentStep === 6) {
                const remainingDepth = store.get(recoverHistoryDepthAtom);
                setRecoveryExiting(true);
                window.history.go(-remainingDepth);
                return;
            }

            // Step5 — device back 은 useBackInterceptor 가 메시지 기반으로 가로채므로 여기 도달 안 함.
            //   안전망: 외부 history 조작 등으로 도달 시 안전하게 팝업만 띄움.
            if (currentStep === 5) {
                setAbortOpen(true);
                return;
            }

            // 실제 history entry 가 한 칸 줄었으므로 depth 도 감소
            decrementDepth();
            const prev = popStep();
            if (prev === null) {
                navigate(-1);
            }
        };
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [popStep, navigate, redirectToSignIn, decrementDepth]);

    // step 전환 시 스크롤 초기화
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    if (redirectToSignIn) return null;

    return (
        <div className="account-recovery-wrapper">
            <Header
                title={t("recover.headerTitle")}
                leftBtn={hideBackBtnSteps.includes(step) ? undefined : { svg: Svgs.IcBack24, func: handleBack }}
            />
            {step === 1 && <RecoverStep1 />}
            {step === 2 && <RecoverStep2 />}
            {step === 3 && <RecoverStep3 />}
            {step === 4 && <RecoverStep4 />}
            {step === 5 && <RecoverStep5 />}
            {step === 6 && <RecoverStep6 />}

            <ConfirmPopup
                isOpen={abortOpen}
                setIsOpen={setAbortOpen}
                title={t("recover.abortPopup.title")}
                message={t("recover.abortPopup.message")}
                leftBtnText={t("recover.abortPopup.exit")}
                onLeft={() => { setAbortOpen(false); exitRecovery(); }}
                rightBtnText={t("recover.abortPopup.continue")}
                onRight={() => setAbortOpen(false)}
                defaultFocus="right"
            />
        </div>
    );
};
